import { makeStyles } from '@mui/styles';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import HierarchyTreeItem from './HierarchyTreeItem';
import SearchModal from './SearchModal';
import ShowMoreButton from './ShowMoreButton';
import { addExpandedNodes } from '../../../actions/ProductHierarchyActions';
import { getUITA } from '../../../utils/commonUtils';
import { getNodeInfoById } from '../../../utils/productHierarchyUtils';
import { ARIA_LABELS, NODE_NAMES } from '../constants';

const useStyles = makeStyles({
  container: {
    marginTop: 24,
    paddingRight: 16,
    paddingBottom: 5,
    height: '100%',
    overflowY: 'auto',

    '@media (max-width: 900px)': {
      maxHeight: '500px',
    },
  },
});

const renderTreeItems = ({ grandParentId, selectedNode, refsList, handleSelect }) => ({
  itemId,
  code,
  label,
  children,
  entityName,
  totalChildren,
}) => {
  const childrenCount = Array.isArray(children) ? children.length : 0;

  const isShowMoreHidden = entityName === NODE_NAMES.PRODUCT || childrenCount >= totalChildren;

  return (
    <HierarchyTreeItem
      key={itemId}
      id={itemId}
      code={code}
      label={label}
      entityName={entityName}
      childrenCount={childrenCount}
      totalChildren={totalChildren}
      isSelected={itemId === selectedNode}
      handleSelectNode={handleSelect}
      isGrandParent={grandParentId === itemId}
      refsList={refsList}
    >
      <>
        {children.map(
          renderTreeItems({
            selectedNode,
            refsList,
            handleSelect,
          })
        )}
        {!isShowMoreHidden && <ShowMoreButton itemId={itemId} entityName={entityName} />}
      </>
    </HierarchyTreeItem>
  );
};

const CustomHierarchyTree = ({ hierarchy, defaultSelected, setDefaultFormKey, onNodeClick }) => {
  const [selectedNode, setSelectedNode] = useState('');
  const [searchItem, setSearchItem] = useState({});
  const [isModalOpened, setIsModalOpened] = useState(false);

  const refsList = useRef({});

  const classes = useStyles();
  const dispatch = useDispatch();

  useEffect(() => {
    const { node, path } = getNodeInfoById(hierarchy, defaultSelected);

    setSelectedNode(defaultSelected);
    setDefaultFormKey(node.formKey);
    dispatch(addExpandedNodes(path));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = (e, nodeId) => {
    const ariaLabel =
      e.target?.ariaLabel || e.target?.parentNode.ariaLabel || e.target?.firstChild.ariaLabel;

    const { node } = getNodeInfoById(hierarchy, nodeId);

    if (ariaLabel === ARIA_LABELS.SEARCH_MODAL_ICON) {
      setSearchItem(node);
      setIsModalOpened(!isModalOpened);
      return;
    }

    if (nodeId !== selectedNode) {
      setSelectedNode(nodeId);
      //fetch detail form data
      onNodeClick(node);
    }
  };

  const handleViewDetails = ({ childNode }) => {
    dispatch(addExpandedNodes([searchItem.itemId]));

    if (selectedNode !== childNode.itemId) {
      setSelectedNode(childNode.itemId);
      onNodeClick(childNode);
    }

    setTimeout(() => {
      refsList.current[childNode.itemId]?.scrollIntoView();
    }, 500);
  };

  return (
    <div className={classes.container} ui-ta={getUITA('HierarchyTreeView')}>
      {hierarchy.map(
        renderTreeItems({
          grandParentId: hierarchy[0].itemId,
          selectedNode,
          refsList,
          handleSelect,
        })
      )}
      {isModalOpened && (
        <SearchModal
          searchItem={searchItem}
          setIsModalOpened={setIsModalOpened}
          handleViewDetails={handleViewDetails}
          setSelected={setSelectedNode}
        />
      )}
    </div>
  );
};

export default React.memo(CustomHierarchyTree);
